import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// Helper function to fetch file paths (replace with actual API route or static data fetching if needed)
async function fetchMediaPaths() {
  // In a real Next.js app, you might use an API route or getStaticProps
  // to read the directory contents server-side.
  // For this example, we'll hardcode the paths based on the directory listing.

  const trucksAvif = [
    '/images/trucks-avif/1_1920x1080.avif',
    '/images/trucks-avif/2_2040x1530.avif',
    '/images/trucks-avif/3_1920x1080.avif',
    '/images/trucks-avif/4_2040x1530.avif',
    '/images/trucks-avif/5_2016x1134.avif',
    '/images/trucks-avif/6_1320x1216.avif',
    '/images/trucks-avif/7_2016x1512.avif',
    '/images/trucks-avif/8_2040x1530.avif',
    '/images/trucks-avif/9_1600x1200.avif',
    '/images/trucks-avif/10_2040x1530.avif',
    '/images/trucks-avif/11_2040x1530.avif',
    '/images/trucks-avif/12_1078x603.avif',
    '/images/trucks-avif/13_2040x1530.avif',
  ];

  const videos = [
    '/images/videos/1_848x478.mp4', // Format: name_WIDTHxHEIGHT.ext
    '/images/videos/2_1920x1080.mp4',
  ];

  const allPaths = [...trucksAvif, ...videos];

  return allPaths.map(path => {
    if (path.endsWith('.mp4')) {
      // Attempt to parse dimensions from filename (e.g., "name_WIDTHxHEIGHT.mp4")
      const filename = path.substring(path.lastIndexOf('/') + 1);
      const parts = filename.split('_');
      let width = 1920; // Default width
      let height = 1080; // Default height
      if (parts.length >= 2) {
        const dimensionsString = parts[parts.length - 1]; // Get potential "WIDTHxHEIGHT.mp4"
        const dimensionsPart = dimensionsString?.split('.')[0]; // Safely get "WIDTHxHEIGHT"
        if (dimensionsPart) {
          const dimensions = dimensionsPart.split('x');
          if (dimensions.length === 2) {
            const parsedWidth = parseInt(dimensions[0] ?? '', 10); // Use ?? '' for safety
            const parsedHeight = parseInt(dimensions[1] ?? '', 10);
            if (!isNaN(parsedWidth) && !isNaN(parsedHeight)) {
              width = parsedWidth;
              height = parsedHeight;
            }
          }
        }
      }

      return {
        type: 'video',
        width: width, // Use parsed width
        height: height, // Use parsed height
        sources: [
          {
            src: path,
            type: 'video/mp4',
          },
        ],
        // poster: '/path/to/video/poster.jpg', // Optional poster
      };
    } else {
      // For AVIF images, you might also want to parse/provide dimensions
      // for optimal layout, especially if using srcSet later.
      return { src: path };
    }
  });
}


const MediaGallery: React.FC = () => {
  const [index, setIndex] = useState(-1);
  const [slides, setSlides] = useState<any[]>([]); // Use 'any' for simplicity, define a proper type for production

  useEffect(() => {
    fetchMediaPaths().then(mediaSlides => {
      setSlides(mediaSlides);
    });
  }, []);

  const openLightbox = (clickedIndex: number) => {
    setIndex(clickedIndex);
  };

  if (slides.length === 0) {
    return <div>Loading media...</div>;
  }

  return (
    <div>
      {/* Apply gradient and larger size to the title */}
      {/* <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-gradient">
        Media Gallery
      </h2> */}
      {/* Use CSS Grid for thumbnail layout - Max 3 columns on larger screens */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4">
        {slides.map((slide, idx) => (
          <button
            key={slide.src || slide.sources[0].src}
            onClick={() => openLightbox(idx)}
            className="aspect-video w-full h-auto overflow-hidden rounded-lg focus:outline-none shadow-md hover:shadow-lg transition-shadow duration-300 bg-secondary/30 group"
          >
            {slide.type === 'video' ? (
              <div className="relative w-full h-full">
                 <video
                   className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                   preload="metadata" // Load only metadata for preview
                 >
                   <source src={slide.sources[0].src + '#t=0.5'} type={slide.sources[0].type} />
                     Your browser does not support the video tag.
                 </video>
                 {/* Simple play icon overlay */}
                 <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                 </div>
              </div>
            ) : (
               <img
                  src={slide.src}
                  alt={`Media ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy" // Native lazy loading for thumbnails
              />
            )}
          </button>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={slides}
        plugins={[Video, Zoom, Thumbnails, Fullscreen]}
        styles={{ container: { backgroundColor: "rgba(0, 0, 0, .9)" } }} // Darker lightbox background
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 1.5,
          doubleTapDelay: 300,
          doubleClickDelay: 500,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
        video={{
          controls: true,
          playsInline: true,
          autoPlay: true,
          loop: false,
          muted: false,
          preload: 'metadata'
        }}
        thumbnails={{
          position: 'bottom',
          width: 120,
          height: 80,
          border: 1,
          borderRadius: 4,
          padding: 4,
          gap: 16,
          imageFit: 'cover',
          vignette: true,
        }}
        fullscreen={{
          auto: false
        }}
      />
    </div>
  );
};

export default MediaGallery; 